import React, { useRef, useEffect, useState } from 'react';
import { FaMicrophone, FaMicrophoneSlash, FaVideo, FaVideoSlash, FaPhone, FaExpand, FaCompress, FaUsers, FaSignOutAlt, FaTimes, FaUserCircle } from 'react-icons/fa';
import styles from './VideoCall.module.css';

const VideoCall = ({
  localStream,
  remoteStream,
  onEndCall,
  onLeaveCall,
  onEndConference,
  isMuted,
  isVideoOff,
  onToggleMute,
  onToggleVideo,
  callerName,
  isInitiator,
  isConference = false,
  isHost = false,
  participants = [],
  currentUserEmail = ''
}) => {
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const containerRef = useRef(null);
  const [showParticipants, setShowParticipants] = useState(false);

  // Set up local video stream
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  // Set up remote video stream
  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
      remoteVideoRef.current.play().catch(err => {
        console.log('Remote video autoplay issue:', err);
      });
    }
  }, [remoteStream]);

  // Call duration timer
  useEffect(() => {
    const timer = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Format call duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Handle end/leave action based on call type
  const handleEndOrLeave = () => {
    if (isConference) {
      if (isHost) {
        onEndConference?.();
      } else {
        onLeaveCall?.();
      }
    } else {
      onEndCall?.();
    }
  };

  // Get initials from email or name
  const getInitials = (text) => {
    if (!text) return '?';
    // Handle participant object format
    if (typeof text === 'object') {
      text = text.name || text.email || '';
    }
    if (text.includes('@')) {
      return text.charAt(0).toUpperCase();
    }
    const parts = text.split(' ');
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    }
    return text.charAt(0).toUpperCase();
  };

  // Get display name from participant (handles both string and object format)
  const getDisplayName = (participant) => {
    if (!participant) return 'Bilinmeyen';
    // Handle object format {email, name}
    if (typeof participant === 'object') {
      if (participant.name) return participant.name;
      if (participant.email) return participant.email.split('@')[0];
      return 'Bilinmeyen';
    }
    // Handle string format (email)
    if (participant.includes('@')) {
      return participant.split('@')[0];
    }
    return participant;
  };

  // Get participant key for React
  const getParticipantKey = (participant) => {
    if (typeof participant === 'object') {
      return participant.email || participant.name || Math.random().toString();
    }
    return participant;
  };

  // Check if a participant is the current user
  const isCurrentUser = (participant) => {
    if (!currentUserEmail) return false;
    if (typeof participant === 'object') {
      return participant.email === currentUserEmail;
    }
    return participant === currentUserEmail;
  };

  // Get other participants (excluding current user)
  const getOtherParticipants = () => {
    return participants.filter(p => !isCurrentUser(p));
  };

  // Render conference view with Teams-like grid layout
  if (isConference) {
    const otherParticipants = getOtherParticipants();
    const participantCount = participants.length; // Total count including self
    
    return (
      <div className={styles.teamsContainer} ref={containerRef}>
        {/* Top Bar */}
        <div className={styles.teamsTopBar}>
          <div className={styles.meetingInfo}>
            <span className={styles.recordIndicator}>●</span>
            <span className={styles.meetingDuration}>{formatDuration(callDuration)}</span>
          </div>
          <div className={styles.meetingTitle}>Toplantı</div>
          <div className={styles.topBarActions}>
            <button 
              className={`${styles.topBarButton} ${showParticipants ? styles.active : ''}`}
              onClick={() => setShowParticipants(!showParticipants)}
              title="Katılımcılar"
            >
              <FaUsers />
              <span className={styles.participantBadge}>{participantCount}</span>
            </button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className={styles.teamsMainArea}>
          {/* Video Grid */}
          <div className={`${styles.teamsVideoGrid} ${styles[`grid${Math.min(participantCount, 9)}`]}`}>
            {/* Local User Tile - Always first */}
            <div className={`${styles.teamsTile} ${styles.localUserTile}`}>
              <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted
                className={styles.teamsVideo}
                style={{ display: isVideoOff ? 'none' : 'block' }}
              />
              {isVideoOff && (
                <div className={styles.avatarContainer}>
                  <div className={styles.teamsAvatar} style={{ backgroundColor: '#4ECDC4' }}>
                    {getInitials('You')}
                  </div>
                </div>
              )}
              <div className={styles.tileLabelContainer}>
                <span className={styles.tileLabel}>
                  {isMuted && <FaMicrophoneSlash className={styles.mutedBadge} />}
                  Siz {isHost && '(Sunucu)'}
                </span>
              </div>
              {participantCount === 1 && (
                <div className={styles.speakingIndicator}></div>
              )}
            </div>

            {/* Other Participants */}
            {otherParticipants.map((participant, index) => {
              const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F'];
              const bgColor = colors[index % colors.length];
              
              // For the first other participant, show their remote video stream if available
              const showRemoteVideo = index === 0 && remoteStream;
              
              return (
                <div key={getParticipantKey(participant)} className={styles.teamsTile}>
                  {showRemoteVideo ? (
                    <>
                      <video
                        ref={remoteVideoRef}
                        autoPlay
                        playsInline
                        className={styles.teamsVideo}
                      />
                      <div className={styles.tileLabelContainer}>
                        <span className={styles.tileLabel}>{getDisplayName(participant)}</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={styles.avatarContainer}>
                        <div className={styles.teamsAvatar} style={{ backgroundColor: bgColor }}>
                          {getInitials(participant)}
                        </div>
                      </div>
                      <div className={styles.tileLabelContainer}>
                        <span className={styles.tileLabel}>{getDisplayName(participant)}</span>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* Participants Panel */}
          {showParticipants && (
            <div className={styles.teamsParticipantsPanel}>
              <div className={styles.panelHeader}>
                <h3>Katılımcılar ({participantCount})</h3>
                <button onClick={() => setShowParticipants(false)} className={styles.panelClose}>
                  <FaTimes />
                </button>
              </div>
              <div className={styles.panelContent}>
                {/* Current user first */}
                <div className={styles.participantRow}>
                  <div className={styles.participantInfo}>
                    <div className={styles.smallAvatar}>
                      {getInitials('Siz')}
                    </div>
                    <span>Siz</span>
                  </div>
                  {isHost && (
                    <span className={styles.hostLabel}>Sunucu</span>
                  )}
                </div>
                {/* Other participants */}
                {otherParticipants.map((participant) => (
                  <div key={getParticipantKey(participant)} className={styles.participantRow}>
                    <div className={styles.participantInfo}>
                      <div className={styles.smallAvatar}>
                        {getInitials(participant)}
                      </div>
                      <span>{getDisplayName(participant)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Bottom Control Bar */}
        <div className={styles.teamsControlBar}>
          <div className={styles.controlsCenter}>
            <button
              className={`${styles.teamsControlBtn} ${isMuted ? styles.off : ''}`}
              onClick={onToggleMute}
              title={isMuted ? 'Mikrofonu aç' : 'Mikrofonu kapat'}
            >
              {isMuted ? <FaMicrophoneSlash /> : <FaMicrophone />}
            </button>

            <button
              className={`${styles.teamsControlBtn} ${isVideoOff ? styles.off : ''}`}
              onClick={onToggleVideo}
              title={isVideoOff ? 'Kamerayı aç' : 'Kamerayı kapat'}
            >
              {isVideoOff ? <FaVideoSlash /> : <FaVideo />}
            </button>

            <button
              className={styles.teamsControlBtn}
              onClick={toggleFullscreen}
              title={isFullscreen ? 'Tam ekrandan çık' : 'Tam ekran'}
            >
              {isFullscreen ? <FaCompress /> : <FaExpand />}
            </button>

            <button
              className={`${styles.teamsControlBtn} ${styles.leaveBtn}`}
              onClick={handleEndOrLeave}
              title={isHost ? 'Toplantıyı bitir' : 'Ayrıl'}
            >
              {isHost ? <FaPhone /> : <FaSignOutAlt />}
              <span className={styles.leaveBtnText}>{isHost ? 'Bitir' : 'Ayrıl'}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Regular 1-on-1 call view
  return (
    <div className={styles.videoCallContainer} ref={containerRef}>
      <div className={styles.remoteVideoWrapper}>
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          className={styles.remoteVideo}
        />
        {!remoteStream && (
          <div className={styles.waitingMessage}>
            <div className={styles.spinner}></div>
            <p>{callerName} bekleniyor...</p>
          </div>
        )}
        <div className={styles.callInfo}>
          <h3>{callerName}</h3>
          <span className={styles.callDuration}>{formatDuration(callDuration)}</span>
        </div>
      </div>

      <div className={styles.localVideoWrapper}>
        <video
          ref={localVideoRef}
          autoPlay
          playsInline
          muted
          className={styles.localVideo}
        />
        {isVideoOff && (
          <div className={styles.videoOffOverlay}>
            <FaVideoSlash />
          </div>
        )}
      </div>

      <div className={styles.controlsWrapper}>
        <div className={styles.controls}>
          <button
            className={`${styles.controlButton} ${isMuted ? styles.active : ''}`}
            onClick={onToggleMute}
          >
            {isMuted ? <FaMicrophoneSlash /> : <FaMicrophone />}
          </button>
          <button
            className={`${styles.controlButton} ${isVideoOff ? styles.active : ''}`}
            onClick={onToggleVideo}
          >
            {isVideoOff ? <FaVideoSlash /> : <FaVideo />}
          </button>
          <button className={styles.controlButton} onClick={toggleFullscreen}>
            {isFullscreen ? <FaCompress /> : <FaExpand />}
          </button>
          <button
            className={`${styles.controlButton} ${styles.endCallButton}`}
            onClick={onEndCall}
          >
            <FaPhone />
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoCall;
